import { useCallback, useMemo, useState } from "react"

// A minimal, per-browser favorite store — no backend model exists yet for
// favoriting files/folders/albums. This is intentionally the smallest thing
// that works: it does not sync across devices or users, but it lets a user
// mark and find their own favorites within this browser, matching Phase 3's
// "minimum functionality necessary" instruction rather than waiting for a
// full backend feature.
const storageKey = (projectId: string) => `files-favorites:${projectId}`

const readStored = (projectId: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed) : new Set()
  } catch {
    return new Set()
  }
}

export function useFavorites(projectId: string) {
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(() =>
    readStored(projectId)
  )

  const persist = useCallback(
    (next: Set<string>) => {
      setFavoriteKeys(next)
      try {
        window.localStorage.setItem(
          storageKey(projectId),
          JSON.stringify([...next])
        )
      } catch {
        // Storage can be unavailable (private mode, quota) — the in-memory
        // state still works for the rest of this session.
      }
    },
    [projectId]
  )

  const toggleFavorite = useCallback(
    (key: string) => {
      const next = new Set(favoriteKeys)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      persist(next)
    },
    [favoriteKeys, persist]
  )

  const isFavorite = useCallback(
    (key: string) => favoriteKeys.has(key),
    [favoriteKeys]
  )

  // A new version's row id is not the id it was favorited under — uploading
  // a new version (or restoring one) must not silently un-favorite the file.
  const migrateFavorite = useCallback(
    (fromKey: string, toKey: string) => {
      if (fromKey === toKey || !favoriteKeys.has(fromKey)) return
      const next = new Set(favoriteKeys)
      next.delete(fromKey)
      next.add(toKey)
      persist(next)
    },
    [favoriteKeys, persist]
  )

  return useMemo(
    () => ({ favoriteKeys, isFavorite, toggleFavorite, migrateFavorite }),
    [favoriteKeys, isFavorite, toggleFavorite, migrateFavorite]
  )
}
